import React, { useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import AppLayout from '../../components/ui/AppLayout';
import PageHeader from '../../components/ui/PageHeader';
import ParceirosCard from '../../components/ParceirosCard';
import SectionCard from '../../components/ui/SectionCard';
import AvatarInput from '../../components/AvatarInput';
import MyPayments from '../Payments/MyPayments';
import BiometriaCard from '../../components/BiometriaCard';
import PushAparelhoCard from '../../components/PushAparelhoCard';
import StatCard from '../../components/ui/StatCard';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import SportsSoccerRoundedIcon from '@mui/icons-material/SportsSoccerRounded';
import EmojiEventsRoundedIcon from '@mui/icons-material/EmojiEventsRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { useAuth } from '../../AuthContext';
import api from '../../api';

const toDateInput = (value) => (value ? new Date(value).toISOString().substring(0, 10) : '');

// Números do jogador logado na temporada (presenças, jogos, vitórias, gols e vezes eleito crack).
const MinhaTemporada = ({ sx }) => {
  const [ano, setAno] = useState(''); // vazio: a API escolhe a temporada mais recente
  const [dados, setDados] = useState(null);

  useEffect(() => {
    api
      .get('pelada/estatisticas', { params: ano ? { ano } : {} })
      .then((response) => setDados(response.data))
      .catch(() => setDados(null));
  }, [ano]);

  if (!dados) return null;
  const { eu } = dados;

  return (
    <SectionCard
      sx={sx}
      title="Minha temporada"
      subtitle="Seus números na pelada"
      actions={
        dados.anosDisponiveis?.length > 1 && (
          <TextField select size="small" label="Temporada" value={dados.ano} onChange={(e) => setAno(e.target.value)} sx={{ minWidth: 120 }}>
            {dados.anosDisponiveis.map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
          </TextField>
        )
      }
    >
      <Grid container spacing={2}>
        <Grid item xs={6} sm={4}><StatCard label="Presenças" value={eu.presencas} hint={`de ${dados.geral.peladas} peladas`} icon={<EventAvailableRoundedIcon />} tone="brand" /></Grid>
        <Grid item xs={6} sm={4}><StatCard label="Jogos" value={eu.jogos} icon={<SportsSoccerRoundedIcon />} tone="info" /></Grid>
        <Grid item xs={6} sm={4}><StatCard label="Vitórias" value={eu.vitorias} icon={<EmojiEventsRoundedIcon />} tone="success" /></Grid>
        <Grid item xs={6} sm={6}><StatCard label="Gols" value={eu.gols} icon={<SportsSoccerRoundedIcon />} tone="success" /></Grid>
        <Grid item xs={12} sm={6}><StatCard label="Crack da pelada" value={`${eu.cracks}x`} hint="eleito nas avaliações" icon={<StarRoundedIcon />} tone="gold" /></Grid>
      </Grid>
    </SectionCard>
  );
};

const ProfilePage = () => {
  const { userLogado, updateUser } = useAuth();
  const [form, setForm] = useState({ nome: '', nickName: '', dataNascimento: '', torre: '', apartamento: '', cpf: '' });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    api
      .get('user/me')
      .then((response) => updateUser(response.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setForm({
      nome: userLogado.nome || '',
      nickName: userLogado.nickName || '',
      dataNascimento: toDateInput(userLogado.dataNascimento),
      torre: userLogado.torre || '',
      apartamento: userLogado.apartamento || '',
      cpf: userLogado.cpf || '',
    });
  }, [userLogado]);

  const setField = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await api.put('user', { ...form, cpf: form.cpf.replace(/\D/g, '') || undefined });
      if (response.data.success) {
        updateUser(response.data.data);
        setToast({ severity: 'success', message: 'Perfil atualizado.' });
      }
    } catch (error) {
      setToast({ severity: 'error', message: error.response?.data?.error || 'Erro ao salvar perfil.' });
    }
    setSaving(false);
  };

  return (
    <AppLayout>
      <PageHeader title="Meu perfil" subtitle="Seus dados, seus números na pelada e suas mensalidades." />
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <SectionCard>
            <Stack alignItems="center" spacing={2} sx={{ py: 1 }}>
              <AvatarInput
                src={userLogado.foto}
                name={userLogado.nome}
                size={140}
                onChange={(user) => {
                  updateUser({ foto: user.foto, fotoKey: user.fotoKey });
                  setToast({ severity: 'success', message: user.foto ? 'Foto atualizada.' : 'Foto removida.' });
                }}
                onError={(message) => setToast({ severity: 'error', message })}
              />
              <Divider flexItem />
              <Typography variant="h6" align="center">{userLogado.nome}</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" justifyContent="center" useFlexGap>
                {userLogado.typePlayer && <Chip size="small" label={userLogado.typePlayer === 'M' ? 'Mensalista' : 'Diarista'} color="primary" variant="outlined" />}
                {userLogado.perfil && <Chip size="small" label={userLogado.perfil === 'User' ? 'Jogador' : userLogado.perfil} />}
                {userLogado.status && <Chip size="small" label={userLogado.status} color={userLogado.status === 'Ativo' ? 'success' : 'default'} variant="outlined" />}
              </Stack>
            </Stack>
          </SectionCard>
          <BiometriaCard sx={{ mt: 3 }} onMessage={(message, severity = 'success') => setToast({ severity, message })} />
          <PushAparelhoCard sx={{ mt: 3 }} onMessage={(message, severity = 'success') => setToast({ severity, message })} />
        </Grid>
        <Grid item xs={12} md={8}>
          <ParceirosCard ano={new Date().getFullYear()} sx={{ mb: 3 }} />
          <SectionCard title="Dados pessoais" actions={<Button variant="contained" onClick={handleSave} disabled={saving}>Salvar alterações</Button>}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}><TextField label="Nome completo" value={form.nome} onChange={setField('nome')} fullWidth /></Grid>
              <Grid item xs={12} sm={4}><TextField label="Apelido" value={form.nickName} onChange={setField('nickName')} fullWidth /></Grid>
              <Grid item xs={12} sm={4}><TextField label="Data de nascimento" type="date" value={form.dataNascimento} onChange={setField('dataNascimento')} InputLabelProps={{ shrink: true }} fullWidth /></Grid>
              <Grid item xs={6} sm={4}>
                <TextField select label="Torre" value={form.torre} onChange={setField('torre')} fullWidth>
                  <MenuItem value="Acqua">Acqua</MenuItem>
                  <MenuItem value="Natura">Natura</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6} sm={4}><TextField label="Apartamento" value={form.apartamento} onChange={setField('apartamento')} fullWidth /></Grid>
              <Grid item xs={12} sm={6}><TextField label="E-mail" value={userLogado.email || ''} disabled fullWidth /></Grid>
              <Grid item xs={12} sm={6}><TextField label="CPF" value={form.cpf} onChange={setField('cpf')} helperText="Usado para gerar o PIX das mensalidades" fullWidth /></Grid>
            </Grid>
          </SectionCard>
          <MinhaTemporada sx={{ mt: 3 }} />
          <MyPayments sx={{ mt: 3 }} />
        </Grid>
      </Grid>
      <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert variant="filled" severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert> : <span />}
      </Snackbar>
    </AppLayout>
  );
};

export default ProfilePage;
